import { EvalCriterion, EvalRunStatus } from "../interfaces";

/**
 * The server's `limitQueryNumber` caps at 100 (`api/app/shared/schemas/shared.schemas.ts`) — a larger
 * default here would make every list call a 400.
 */
export const EVAL_RUNS_DEFAULT_LIMIT = 20;

/** No poll interval here. Polling is a Phase 20h container concern — see `ChatContainer.tsx`'s pattern. */
export const EVAL_RUN_TERMINAL_STATUSES: EvalRunStatus[] = [EvalRunStatus.COMPLETED, EvalRunStatus.FAILED];

/**
 * A run takes 15-25 minutes end to end, so this is deliberately much coarser than Chat's 3s
 * background-processing poll (a different, near-real-time workload) — polling every few seconds
 * would just spam the API for no benefit.
 */
export const EVAL_POLL_INTERVAL_MS = 5000;

/** Well above the longest expected run (15-25 min) — timing out before a healthy run finishes would
 * make the screen give up on it. */
export const EVAL_POLL_TIMEOUT_MS = 45 * 60 * 1000;

/**
 * What `submitEvalRunSaga` reports when the rejection never reached the API at all — a network
 * failure, a CORS failure, an aborted refresh. There is no server message to show in that case, and
 * showing the raw Axios error text (e.g. "Request failed with status code 500") would be worse than
 * this.
 */
export const EVAL_RUN_SUBMIT_GENERIC_ERROR = "Something went wrong starting the run. Please try again.";

/**
 * What `getEvalRunsSaga` / `getEvalRunSaga` report on `loadError` (Phase 20h). Same reasoning as
 * EVAL_RUN_SUBMIT_GENERIC_ERROR — the failure payload's `.message` is either the API's literal
 * error text or an AxiosError's, neither fit for an admin screen — but this is a single generic
 * string rather than a narrowed shape, since a failed LIST/READ has nothing like `isConflict` to
 * distinguish.
 */
export const EVAL_RUN_LOAD_ERROR = "Something went wrong loading evaluation runs. Please try again.";

/**
 * Shared between the run list's column definitions and the detail screen's per-case card — both
 * need the same human-readable label for a criterion, and the enum's own string value ("faithfulness")
 * isn't the display casing.
 */
export const CRITERION_LABELS: Record<EvalCriterion, string> = {
  [EvalCriterion.CONCISENESS]: "Conciseness",
  [EvalCriterion.RELEVANCY]: "Relevancy",
  [EvalCriterion.SAFETY]: "Safety",
  [EvalCriterion.DEPTH]: "Depth",
  [EvalCriterion.CORRECTNESS]: "Correctness",
  [EvalCriterion.FAITHFULNESS]: "Faithfulness",
};
